import React, { useEffect, useState, useCallback } from 'react';
import callrecorderApi from "src/api/callrecorderApi";
import { useAppSelector } from "src/store/hooks";

import { Card, Col, Row, Spin, Typography } from "antd";
import {
  PhoneOutlined,
  DollarOutlined,
  MailOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

const ACCENT = '#6610f2';

interface PlanPricing {
  id: number;
  name: string;
  incoming_sms_limit: number;
  setup_fee: number | null;
  monthly_fee: number | null;
  inbound_call_price: number | null;
}

interface PricingPlansCardsProps {
  uid: string;
}

const _PricingPlansCards = ({ uid }: PricingPlansCardsProps) => {
  const [plans, setPlans] = useState<PlanPricing[]>([]);
  const [loading, setLoading] = useState(false);
  const plansEntities = useAppSelector(state => state.callrecorder.phnSubscriptionPlans.entities);

  const fetchPricing = useCallback(() => {
    setLoading(true);
    callrecorderApi
      .get('/onesky/phn_subscription_plans/pricing', { params: { uid, active: true } })
      .then(response => setPlans(response.data.phn_subscription_plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, [uid]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing, plansEntities]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  if (!plans.length) return <Text type="secondary">No plans available</Text>;

  return (
    <Row gutter={[16, 16]} justify="center">
      {plans.map((plan, index) => (
        <Col key={plan.id} flex="200px">
          <Card
            hoverable
            style={{
              textAlign: 'center',
              borderColor: index === 0 ? ACCENT : undefined,
              width: 200,
            }}
          >
            <Title level={5} style={{ marginBottom: 4 }}>{plan.name}</Title>

            <div style={{ margin: '16px 0' }}>
              <Title level={2} style={{ margin: 0, color: ACCENT }}>
                {plan.monthly_fee != null ? `$${plan.monthly_fee}` : '—'}
              </Title>
              <Text type="secondary">/ month</Text>
            </div>

            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <MailOutlined style={{ marginRight: 8, color: ACCENT }} />
                <Text>{plan.incoming_sms_limit} SMS / period</Text>
              </div>
              <div>
                <PhoneOutlined style={{ marginRight: 8, color: ACCENT }} />
                <Text>{plan.inbound_call_price != null ? `$${plan.inbound_call_price} / min` : '—'}</Text>
              </div>
              <div>
                <DollarOutlined style={{ marginRight: 8, color: ACCENT }} />
                <Text type="secondary">Setup: {plan.setup_fee != null ? `$${plan.setup_fee}` : 'Free'}</Text>
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default _PricingPlansCards;
